import { ControllerRenderProps, FieldPath, FieldValues } from '@otp/ui';
import React, { useEffect, useMemo } from 'react';
import { useCertificates } from 'src/hooks/query/lms/certificates/use-certificates';
import { DropdownConditional } from '@otp/ui';
import { useTranslation } from 'react-i18next';
import { UseFormSetError } from 'react-hook-form/dist/types/form';
export { validateDropdownConditionalCondition } from '@otp/ui';

type RequiredCertificatesConfigFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  field: ControllerRenderProps<TFieldValues, TName>;
  programId?: string;
  setError?: UseFormSetError<TFieldValues>;
  error?: string;
};

export const RequiredCertificatesConfigField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  field,
  programId,
  setError,
  error,
}: RequiredCertificatesConfigFieldProps<TFieldValues, TName>) => {
  const { t } = useTranslation();
  const { certificates } = useCertificates({
    filter: { programIds: programId ? [programId] : undefined },
    skip: !programId,
  });
  const [defaultValue, setDefaultValue] = React.useState(field.value);

  const entityOptions = useMemo(
    () =>
      (certificates || []).map((entity) => ({
        value: entity.id as string,
        name: entity.name as string,
      })),
    [certificates, programId],
  );

  useEffect(() => {
    if (field.value && !defaultValue) {
      setDefaultValue(field.value);
    }
  }, [field.value]);

  return (
    <DropdownConditional
      key={defaultValue}
      onChange={field.onChange}
      nestedConditionMaxItems={1}
      showExplanation
      topLevelOperators={['AND']}
      nestedLevelOperators={['OR']}
      multiple
      options={entityOptions}
      defaultValue={defaultValue}
      limitTags={3}
      selectClassName={'w-[50%]'}
      explanationPrefix={
        <b>
          {t('lms.curriculum.form.fields.required-certificates-explanation')}
        </b>
      }
      error={error}
    />
  );
};
